import React, { useRef } from "react"
import { Handle, HandleProps } from "reactflow"
import { Grid, Typography, GridProps, IconProps } from "@material-ui/core"

import useElementSize from "@/hooks/useElementSize"

import { Portlet, Divider } from "@/components"

import { DRAG_HANDLE_CLASS_NAME } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/config/flowResources"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBody/styles"

type BlockBodyProps = {
	title: string
	icon?: React.ReactElement
	IconProps?: IconProps
	HeaderProps?: GridProps & { endAdornment?: React.ReactNode }
	HandleProps?: HandleProps & { style?: React.CSSProperties, className?: string }
	footerComponent?: React.ReactNode
}

const BlockBody: React.FC<BlockBodyProps> = (props) => {
	const {
		children,
		title,
		icon,
		IconProps,
		HeaderProps,
		HandleProps,
		footerComponent
	} = props

	const classes = useStyles()

	const headerRef = useRef<HTMLDivElement>(null)
	const headerSize = useElementSize(headerRef)

	/**
	 * We need to calculate the position by hand, since the handle must be outside
	 * the header element in other to be rendered fully on the block (so we are not
	 * able to put the handle inside header element and use a simple position style).
	 */
	const handleTopPosition = headerSize.current.height / 2

	return (
		<>
			<Portlet
				style={{ padding: 0 }}
				elevation={1}
			>
				<Grid
					container
					className={classes.blockContent}
				>
					<Grid
						item
						xs={12}
					>
						<Grid
							container
							alignItems="center"
							justify="flex-start"
							className={`${classes.headerContainer} ${DRAG_HANDLE_CLASS_NAME}`}
							style={HeaderProps?.style}
							ref={headerRef}
						>
							{icon && (
								React.cloneElement(icon, IconProps)
							)}

							<Divider orientation="vertical" size={2} />

							<Typography
								variant="h2"
								color="textPrimary"
								className={classes.title}
							>
								{title}
							</Typography>

							{HeaderProps?.endAdornment && (
								<>
									<Divider orientation="vertical" size={2} />

									{HeaderProps?.endAdornment}
								</>
							)}
						</Grid>
					</Grid>

					<Grid
						item
						xs={12}
					>
						<Grid
							container
							className={classes.contentContainer}
							spacing={2}
						>
							{children}
						</Grid>
					</Grid>

					{footerComponent && (
						<Grid
							item
							xs={12}
						>
							{footerComponent}
						</Grid>
					)}
				</Grid>
			</Portlet>

			{HandleProps && (
				<Handle
					{...HandleProps}
					style={{
						...HandleProps.style,
						top: handleTopPosition
					}}
				/>
			)}
		</>
	)
}

export default BlockBody
